import { useCallback } from 'react';
import { toQS } from './urls';

type UseSocialAuthVariables = {
  signInUrl?: string
}

type InitAuthFlowVariables = {
  social: 'google' | 'facebook' | 'apple'
  onAuthSuccess: (getTokenResult: GetTokenResult) => void
  onError: (error: string) => void
  // Called with the sign-in URL when the browser blocks the popup, e.g. to sign in through the current tab instead
  onPopupBlocked?: (socialAuthUrl: string) => void
}
type GetTokenResult = { data: { token: string } }

const objectIsGetTokenResult = (object: unknown): object is GetTokenResult => typeof object === 'object' && object !== null && 'data' in object && typeof object.data === 'object' && object.data !== null && 'token' in object.data && typeof object.data.token === 'string';

export const makeQueryString = (social: InitAuthFlowVariables['social'], returnPath?: string) => {
  switch (social) {
    case 'facebook':
      return toQS({
        access_type: ' ',
        prompt: ' ',
        scope: undefined,
        ...(returnPath && { returnPath }),
      });
    case 'google':
      return toQS({
        access_type: 'offline',
        prompt: 'consent',
        scope: undefined,
        ...(returnPath && { returnPath }),
      });
    case 'apple':
      return toQS({
        ...(returnPath && { returnPath }),
      });
    default:
      throw new Error(`useSocialAuth failed makeQueryString. Hook must be called with "google", "facebook", or "apple". got ${social}`);
  }
};

export const makeAuthPath = (social: InitAuthFlowVariables['social']) => {
  switch (social) {
    case 'facebook':
      return '/sign-in/facebook';
    case 'google':
      return '/sign-in/google';
    case 'apple':
      return '/sign-in/apple';
    default:
      throw new Error(`useSocialAuth failed makeAuthPath. Hook must be called with "google", "facebook", or "apple". got ${social}`);
  }
};

export const useSocialAuth = ({ signInUrl }: UseSocialAuthVariables) => {
  const kickoffExternalAuthFlow = useCallback(async ({ social, onAuthSuccess, onError, onPopupBlocked }: InitAuthFlowVariables) => {
    if (!signInUrl) return;

    const queryString = makeQueryString(social, window.location.pathname);
    const authPath = makeAuthPath(social);
    const socialAuthUrl = `${signInUrl}${authPath}?${queryString}`;
    const socialAuthWindow = window.open(socialAuthUrl, '_blank', 'menubar=no,location=no,resizable=yes,scrollbars=no');
    if (!socialAuthWindow) {
      onPopupBlocked?.(socialAuthUrl);
      return;
    }
    if (typeof window !== 'undefined' && socialAuthWindow) {
      const postRobot = await import(/* webpackChunkName: "postRobot" */ 'post-robot');
      const handleGetTokenSuccess = (getTokenResult: unknown) => {
        if (objectIsGetTokenResult(getTokenResult)) {
          onAuthSuccess(getTokenResult);
        }
        socialAuthWindow.close();
      };
      const handleGetTokenError = (err: string) => {
        console.log(`Unhandled error: ${err}`);
        onError(err);
      };
      const onPing = () => postRobot
        .send(socialAuthWindow, 'getToken', {}, { domain: signInUrl })
        .then(handleGetTokenSuccess)
        .catch(handleGetTokenError);
      void postRobot.once('ping', { window: socialAuthWindow }, onPing);
    }
  }, [signInUrl]);
  return { kickoffExternalAuthFlow };
};
